import React, { Fragment } from "react";
import { useFormikContext } from "formik";
import { BUTTON_CLASS_CONTEXT, UI_THEME } from "utils/constants/ui";
import get from "lodash/get";
import MenuNavHeader from "components/MenuNavHeader";
import MenuNavList from "components/FormerEditor/MenuNavList";
import Button from "components/Form/fields/Button";
import InfoNotice from "components/InfoNotice";
import FieldsPicker from "../FieldsPicker";
import CombinationsList from "../CombinationsList";
import {
  SET_PRODUCTS_FIELDS_GUIDANCE,
  RESET_COMBINATIONS_WARNING,
  SET_PRODUCTS_COMBINATIONS_GUIDANCE,
  SET_PRODUCTS_SETTINGS_GUIDANCE,
  CHECKOUT_PRODUCT_SETTINGS_INTENT,
  NO_ELIGIBLE_COMBINATION_FIELD_WARNING
} from "utils/constants/checkout";
import { useFieldGroupsFields } from "utils/hooks/pages";
import { STATE_KEYS } from "utils/constants/state";
import { EDITOR_MODE } from "utils/constants/editor";
import Settings from "./Settings";
import { COMBINATION_FIELDS } from "utils/constants/form";
import { setEditorFocus } from "utils/builder";
import { getFormFieldGroupAddFieldFocus } from "utils/hooks/focus";
import { getFormEntity } from "utils/hooks/manifest";
import { getIconForUID } from "components/FormerEditor/utils";
import VideoModal from "components/VideoModal";
import { COMMAND_CONTENT_ID } from "components/CommandPalette/constants";
const ForwardIcon = getIconForUID("forward");

const ProductsSettings = (props) => {
  const { values, setValues, setFieldValue } = useFormikContext();

  const intent = get(values, STATE_KEYS.EDITOR.MENU_THIRD_INTENT);
  const inspectModeActive =
    get(values, STATE_KEYS.EDITOR.MODE) === EDITOR_MODE.INSPECT;
  const setModeAndIntent = (updatedIntent) => {
    if (inspectModeActive) {
      setFieldValue(STATE_KEYS.EDITOR.MODE, EDITOR_MODE.USE);
    }
    setFieldValue(STATE_KEYS.EDITOR.MENU_THIRD_INTENT, updatedIntent);
  };
  const formEntity = getFormEntity(values);
  const formEntityUUID = formEntity && formEntity.uuid;

  const addFieldFocus = getFormFieldGroupAddFieldFocus({
    uuid: formEntityUUID
  });

  const logicFieldsPath = `${props.name}.${STATE_KEYS.FORM.CONFIG_LOGIC_CHECKOUTS_FIELDS}`;
  const logicFields = get(values, logicFieldsPath, []);
  const hasLogicFields = logicFields.length > 0;

  const allFields = useFieldGroupsFields(props.name);
  const hasFields = allFields.length > 0;
  const hasCombinationFields =
    allFields.filter((field) => COMBINATION_FIELDS[field.uid]).length > 0;

  let content;
  if (!intent) {
    content = (
      <Fragment>
        <MenuNavHeader label="Products" />
        <MenuNavList
          config={[
            {
              label: "Logic",
              items: [
                {
                  label: "Fields",
                  description: SET_PRODUCTS_FIELDS_GUIDANCE,
                  disabled: !hasFields,
                  onClick: () =>
                    setModeAndIntent(
                      CHECKOUT_PRODUCT_SETTINGS_INTENT.SET_FIELDS
                    )
                },
                {
                  label: "Combinations",
                  description: SET_PRODUCTS_COMBINATIONS_GUIDANCE,
                  disabled: !hasLogicFields,
                  onClick: () =>
                    setModeAndIntent(
                      CHECKOUT_PRODUCT_SETTINGS_INTENT.SET_COMBINATIONS
                    )
                }
              ]
            },
            {
              label: "Other",
              items: [
                {
                  label: "Settings",
                  description: SET_PRODUCTS_SETTINGS_GUIDANCE,
                  disabled: false,
                  onClick: () =>
                    setModeAndIntent(
                      CHECKOUT_PRODUCT_SETTINGS_INTENT.SET_SETTINGS
                    )
                }
              ]
            }
          ]}
        />
      </Fragment>
    );
  } else if (intent === CHECKOUT_PRODUCT_SETTINGS_INTENT.SET_FIELDS) {
    let pickerNotice;
    if (hasFields) {
      const infoNoticeProps = {
        hideIcon: true,
        customClasses: {
          container:
            "w-100 flex flex-column pa2 items-start bg-content--secondary bb bh--inset hairline-1"
        }
      };

      pickerNotice = hasCombinationFields ? (
        <InfoNotice
          {...infoNoticeProps}
          copy={`Note: ${RESET_COMBINATIONS_WARNING}`}
        >
          <div
            onClick={() =>
              setModeAndIntent(
                CHECKOUT_PRODUCT_SETTINGS_INTENT.SET_COMBINATIONS
              )
            }
            className="f6 fw5 pl2 link__underline color-primary pointer flex items-center pt1"
          >
            Combinations
            <div className="pl1 flex">
              <ForwardIcon />
            </div>
          </div>
        </InfoNotice>
      ) : (
        <InfoNotice
          {...infoNoticeProps}
          copy={NO_ELIGIBLE_COMBINATION_FIELD_WARNING}
        >
          <div
            onClick={setEditorFocus({
              values,
              setValues,
              focus: addFieldFocus
            })}
            className="f6 fw5 pl2 pt1 link__underline color-primary pointer"
          >
            Add field
          </div>
        </InfoNotice>
      );
    }

    content = (
      <Fragment>
        <MenuNavHeader
          label="Fields"
          action={
            <VideoModal
              copy="Learn"
              id={COMMAND_CONTENT_ID.CHECKOUT.SETUP_FIELD_COMBINATIONS}
            />
          }
        />
        {pickerNotice}
        <div className="w-100 bg-white bh--inset relative pa3 pt4">
          <FieldsPicker {...props} />
          <div className="pt3 flex justify-end">
            <div className="pr2">
              <Button
                disabled={!hasCombinationFields}
                copy="Clear"
                theme={UI_THEME.SLIM}
                onClick={() => setFieldValue(logicFieldsPath, [])}
                classes={{
                  contextClass: BUTTON_CLASS_CONTEXT.SECONDARY
                }}
              ></Button>
            </div>
            <Button
              disabled={!hasLogicFields}
              onClick={() => {
                setModeAndIntent(
                  CHECKOUT_PRODUCT_SETTINGS_INTENT.SET_COMBINATIONS
                );
              }}
              theme={UI_THEME.SLIM}
              copy="Next"
            ></Button>
          </div>
        </div>
      </Fragment>
    );
  } else if (intent === CHECKOUT_PRODUCT_SETTINGS_INTENT.SET_COMBINATIONS) {
    content = <CombinationsList {...props} />;
  } else if (intent === CHECKOUT_PRODUCT_SETTINGS_INTENT.SET_SETTINGS) {
    content = <Settings {...props} />;
  }

  return content;
};

export default ProductsSettings;
